import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { pricingData, processSteps, features, supervisors } from '../data/mock';
import { 
  FileText, 
  CheckCircle2, 
  ArrowRight, 
  Building2,
  MessageSquare,
  Zap,
  Wallet,
  Stethoscope,
  Scale,
  TrendingUp,
  Monitor,
  Home,
  BookOpen,
  Users,
  HeadphonesIcon,
  AlertCircle,
  Search,
  UserX,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Professional categories for hero section with qualifications
const PROFESSIONAL_CATEGORIES = [
  { icon: Stethoscope, label: "医療分野", sub: "医師・看護師など", color: "bg-rose-50 text-rose-600" },
  { icon: Scale, label: "法律・士業", sub: "弁護士・社労士など", color: "bg-slate-100 text-slate-700" },
  { icon: TrendingUp, label: "金融・保険", sub: "FP・CFPなど", color: "bg-emerald-50 text-emerald-600" },
  { icon: Monitor, label: "IT・BtoB", sub: "ITコンサルなど", color: "bg-blue-50 text-blue-600" },
  { icon: Home, label: "不動産・住宅", sub: "宅建士・建築士など", color: "bg-amber-50 text-amber-600" },
  { icon: BookOpen, label: "生活・専門", sub: "専門資格保有者", color: "bg-purple-50 text-purple-600" }
];

// Client logos for trust section - with size adjustments
const CLIENT_LOGOS = [
  { url: "/images/assets/287z4m7r-s-166x26-b19c443c-660a-4a50-b13c-419f2e755ddf.svg", alt: "Digital Trends", size: "h-5 md:h-6" },
  { url: "/images/assets/qqgrq40a-s-412x44-webp-b65ff089-91d4-42a0-a27f-36bc31b5dc7d.png", alt: "SMS", size: "h-6 md:h-8" },
  { url: "/images/assets/qtlos8dp-s-591x228-webp-2ceddfd3-04d6-4896-bd01-4fe70c57f3bb.png", alt: "Media Engine", size: "h-6 md:h-8" },
  { url: "/images/assets/4a58dvrp-anabuki-logo.webp", alt: "あなぶき興産", size: "h-5 md:h-6" },
  { url: "/images/assets/0dm9eqbh-s-1000x300-v-fs-webp-a6a84dd5-cee2-442e-8e8b-22ab603243f4.png", alt: "Basic", size: "h-6 md:h-8" }
];

// FAQ data
const FAQ_DATA = [
  {
    question: "監修者と直接やり取りが必要ですか？",
    answer: "原則不要です。要件整理・調整・進行管理は当社が代行します。"
  },
  {
    question: "どんな分野でも対応できますか？",
    answer: "基本的には対応できますので、まずはご相談ください。監修者候補の中から、テーマに適合する人材をご提案します。"
  },
  {
    question: "1本だけ試せますか？",
    answer: "可能です。月額・初期費用はかかりません。"
  },
  {
    question: "監修者としてプロフィール掲載（実名・顔写真）は必須ですか？",
    answer: "基本は掲載する場合が多いですが、方針に合わせて調整します。公開範囲や表記についてもご相談ください。"
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Supervisors scroll ref
  const supervisorsScrollRef = useRef(null);
  
  const scrollSupervisors = (direction) => {
    if (supervisorsScrollRef.current) {
      const scrollAmount = 280; // card width + gap
      supervisorsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // 送信先は同じサイト内の Cloudflare Pages Functions（frontend/functions/api/contact.js）
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('送信に失敗しました');
      }
      
      // Store form data for thank you page
      sessionStorage.setItem('contactFormData', JSON.stringify(formData));
      navigate('/thanks');
    } catch (error) {
      console.error('Contact form submission error:', error);
      alert('送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const featureIcons = [
    <Building2 className="w-6 h-6" key="building" />,
    <MessageSquare className="w-6 h-6" key="message" />,
    <Zap className="w-6 h-6" key="zap" />,
    <Wallet className="w-6 h-6" key="wallet" />,
    <Users className="w-6 h-6" key="users" />,
    <HeadphonesIcon className="w-6 h-6" key="headphones" />
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <a href="#" className="flex items-center gap-2">
              <img 
                src="/images/assets/hupg3mur-Group.svg" 
                alt="記事監修マッチ" 
                className="h-5 md:h-6"
              />
              <span className="text-xs text-slate-500 hidden sm:inline whitespace-nowrap">by ミライトマッチ</span>
            </a>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#service" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">サービス</a>
            <a href="#features" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">選ばれる理由</a>
            <a href="#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">費用・相場</a>
            <a href="#flow" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">依頼の流れ</a>
            <a href="#faq" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">FAQ</a>
          </nav>
          <Button 
            onClick={scrollToContact}
            className="free_consultation_button bg-slate-800 hover:bg-slate-700 text-white px-5 py-2 rounded-lg transition-all duration-300 hover:shadow-lg text-sm"
          >
            無料で相談する
          </Button>
        </div>
      </header>

      {/* Hero Section - Fits in one screen */}
      <section className="pt-16 md:pt-20 pb-6 md:pb-8 px-6 min-h-[calc(100vh-60px)] flex items-center relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-teal-50/30 to-slate-50"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-teal-100/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-slate-100/60 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-teal-50/50 rounded-full blur-2xl"></div>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230d9488' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            {/* Left content */}
            <div>
              <p className="text-sm md:text-base text-teal-600 font-semibold mb-2">
                E-E-A-T（経験・専門性・権威性・信頼性）を向上！
              </p>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-slate-800 leading-tight mb-4">
                専門家に依頼・発注できる<br />記事監修サービス
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
                月額・初期費用0円、1記事単位・最短即日で依頼可能
              </p>
              
              <ul className="space-y-2 mb-5">
                <li className="flex items-start gap-2 text-slate-700 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span>500名以上の監修者候補から、テーマに最適な人材を選定</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span>やり取り・期限管理・修正整理までディレクションを代行</span>
                </li>
              </ul>

              <Button 
                onClick={scrollToContact}
                size="lg"
                className="free_consultation_button bg-teal-600 hover:bg-teal-700 text-white px-8 py-5 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              >
                無料で相談する
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Right - Professional Categories with Icons */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-3 gap-4">
                {PROFESSIONAL_CATEGORIES.map((category, index) => {
                  const IconComponent = category.icon;
                  return (
                    <div key={index} className="group">
                      <div className={`${category.color} rounded-xl p-5 shadow-lg border border-slate-200 transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl`}>
                        <div className="flex flex-col items-center text-center">
                          <div className="w-10 h-10 flex items-center justify-center mb-2">
                            <IconComponent className="w-7 h-7" />
                          </div>
                          <span className="font-medium text-sm">
                            {category.label}
                          </span>
                          <span className="text-[10px] text-slate-500 mt-1">
                            {category.sub}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile - 2x3 Grid */}
            <div className="lg:hidden">
              <div className="grid grid-cols-3 gap-2 justify-items-center">
                {PROFESSIONAL_CATEGORIES.map((category, index) => {
                  const IconComponent = category.icon;
                  return (
                    <div key={index}>
                      <div className={`${category.color} rounded-xl p-3 shadow-lg border border-slate-200 w-[100px]`}>
                        <div className="flex flex-col items-center text-center">
                          <div className="w-6 h-6 flex items-center justify-center mb-1">
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <span className="font-medium text-[10px] leading-tight">
                            {category.label}
                          </span>
                          <span className="text-[8px] text-slate-500 mt-0.5">
                            {category.sub}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos / Trust Section */}
      <section className="py-10 px-6 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-slate-700 text-lg md:text-xl font-medium mb-8">
            ミライトマッチは、上場企業や大手マーケティング会社など <span className="font-bold text-teal-600">600社以上</span>にご利用いただいております。
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
            {CLIENT_LOGOS.map((logo, index) => (
              <div key={index} className={`${logo.size} flex items-center`}>
                <img 
                  src={logo.url} 
                  alt={logo.alt}
                  className="h-full w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section - こんなお悩みありませんか？ */}
      <section id="problems" className="py-16 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-10 text-center">
            こんなお悩みありませんか？
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Pain Point 1 */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  生成AIで執筆スピードが上がった
                  <br />
                  <span className="text-slate-600">でも、品質担保の負担が増えた</span>
                </h3>
                <p className="text-sm text-slate-500">
                  情報の正確性確認や監修対応の負担が増加
                </p>
              </CardContent>
            </Card>

            {/* Pain Point 2 */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserX className="w-7 h-7 text-rose-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  社内に記事品質を担保できる
                  <br />
                  専門家がいない
                </h3>
                <p className="text-sm text-slate-500">
                  専門知識を持つ監修者の確保が難しい
                </p>
              </CardContent>
            </Card>

            {/* Pain Point 3 */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  専門家を探すのに
                  <br />
                  一苦労
                </h3>
                <p className="text-sm text-slate-500">
                  分野ごとの窓口分散、調整コストが大きい
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section - サービス紹介 */}
      <section id="service" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <img 
                src="/images/assets/kansyuu-service-overview.webp"
                width="1200"
                height="655"
                loading="lazy"
                alt="記事監修マッチのサービス概要 - 監修者の選定から進行管理まで一括代行"
                className="w-full max-w-md rounded-xl shadow-lg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm text-teal-600 font-semibold mb-2">
                記事監修マッチなら
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6">
                丸投げOK！
                <br />
                監修を<span className="text-teal-600">「探す」</span>から<span className="text-teal-600">「任せる」</span>へ
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                記事監修マッチは、監修者の手配だけで終わらせません。
                テーマ整理、監修者選定、進行管理、修正整理までを一括で代行し、
                監修業務そのものを"回る仕組み"として提供します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-10 text-center">
            記事監修マッチが選ばれる理由
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600 flex-shrink-0">
                      {featureIcons[index]}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-800 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Supervisors Section - 記事監修者一覧 */}
      <section id="supervisors" className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3 text-center">
            記事監修者一覧
          </h2>
          <p className="text-slate-600 text-center mb-10 text-sm">
            各分野の専門家が記事の信頼性を高めます
          </p>
          
          {/* Horizontal Scroll Container */}
          <div className="relative">
            {/* Left Arrow Button - PC only */}
            <button
              onClick={() => scrollSupervisors('left')}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-md items-center justify-center hover:bg-slate-50 hover:shadow-lg transition-all duration-200"
              aria-label="前へスクロール"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            
            {/* Scroll Container */}
            <div 
              ref={supervisorsScrollRef}
              className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide"
            >
              <div className="flex gap-5 w-max">
                {supervisors.map((supervisor) => (
                  <div 
                    key={supervisor.id}
                    className="flex-shrink-0 w-64 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                  >
                    {/* Photo - Reduced height */}
                    <div className="h-48 bg-slate-100 relative overflow-hidden">
                      <img 
                        src={supervisor.photo}
                        alt={supervisor.nameEn}
                        className="w-full h-full object-cover object-[50%_10%]"
                      />
                      {/* Field Badge */}
                      <span className="absolute top-3 left-3 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                        {supervisor.field}
                      </span>
                    </div>
                    
                    {/* Info - Expanded area */}
                    <div className="p-4">
                      <h3 className="text-base font-bold text-slate-800 mb-1">{supervisor.nameEn}</h3>
                      <p className="text-sm text-teal-600 font-medium mb-2">
                        {supervisor.title}
                        {supervisor.credentials && (
                          <span className="text-slate-500 font-normal"> / {supervisor.credentials}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {supervisor.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Arrow Button - PC only */}
            <button
              onClick={() => scrollSupervisors('right')}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-md items-center justify-center hover:bg-slate-50 hover:shadow-lg transition-all duration-200"
              aria-label="次へスクロール"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          
          <p className="text-center text-slate-500 text-xs mt-6 md:hidden">
            ← 横にスクロールして他の監修者を見る →
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3 text-center">
            記事監修の費用・相場
          </h2>
          <p className="text-center text-teal-600 font-medium mb-8">
            月額費用0円・初期費用0円・1記事から依頼可能
          </p>
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-700 font-semibold py-3 px-4">代表的な監修内容</TableHead>
                  <TableHead className="text-slate-700 font-semibold py-3 px-4">料金目安（税込）</TableHead>
                  <TableHead className="text-slate-700 font-semibold py-3 px-4">主な監修者例</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingData.map((item, index) => (
                  <TableRow key={index} className="hover:bg-slate-50">
                    <TableCell className="py-3 px-4 text-slate-800 font-medium">{item.category}</TableCell>
                    <TableCell className="py-3 px-4 text-slate-700">{item.priceRange}</TableCell>
                    <TableCell className="py-3 px-4 text-slate-600">{item.examples}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="mt-5 text-sm text-slate-500 space-y-1">
            <p>※上記はあくまで目安です。</p>
            <p>記事の専門性、監修範囲、納期、監修者の資格・実務経験により金額は変動します。</p>
            <p>最終的な料金は個別にお見積りします。</p>
            <p className="font-medium text-slate-600">月額費用・初期費用はかかりません。</p>
          </div>
        </div>
      </section>

      {/* Process Section - Arrow/Chevron Style with equal widths */}
      <section id="flow" className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-10 text-center">
            記事監修の依頼から納品までの流れ
          </h2>
          
          {/* Desktop Chevron Arrows - 6 steps */}
          <div className="hidden md:flex items-stretch justify-center">
            {processSteps.map((item, index) => {
              // Gradient colors from light to dark (step 1 to 6)
              const colors = [
                { from: '#5eead4', to: '#2dd4bf' },  // Step 1 - lightest
                { from: '#2dd4bf', to: '#14b8a6' },  // Step 2
                { from: '#14b8a6', to: '#0d9488' },  // Step 3
                { from: '#0d9488', to: '#0f766e' },  // Step 4
                { from: '#0f766e', to: '#115e59' },  // Step 5
                { from: '#115e59', to: '#134e4a' }   // Step 6 - darkest
              ];
              return (
                <div key={index} className="relative flex-1 max-w-[160px]">
                  <div 
                    className="relative text-white px-3 py-5 h-full flex flex-col justify-center"
                    style={{
                      clipPath: index === 0 
                        ? 'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)'
                        : index === processSteps.length - 1
                          ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 16px 50%)'
                          : 'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%, 16px 50%)',
                      marginLeft: index > 0 ? '-1px' : '0',
                      background: `linear-gradient(135deg, ${colors[index].from} 0%, ${colors[index].to} 100%)`
                    }}
                  >
                    <div className="text-center">
                      <span className="text-2xl font-bold opacity-60 block">{item.step}</span>
                      <span className="text-xs font-semibold leading-tight block mt-1">{item.title}</span>
                      <p className="text-[10px] text-teal-100 mt-1 leading-relaxed">
                        {item.hasLink ? (
                          <a href="#contact" className="underline hover:text-white">フォーム</a>
                        ) : null}
                        {item.hasLink ? 'から' : item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Mobile version - Vertical */}
          <div className="md:hidden space-y-2">
            {processSteps.map((item, index) => {
              // Gradient colors from light to dark (step 1 to 6)
              const colors = [
                { from: '#5eead4', to: '#2dd4bf' },  // Step 1 - lightest
                { from: '#2dd4bf', to: '#14b8a6' },  // Step 2
                { from: '#14b8a6', to: '#0d9488' },  // Step 3
                { from: '#0d9488', to: '#0f766e' },  // Step 4
                { from: '#0f766e', to: '#115e59' },  // Step 5
                { from: '#115e59', to: '#134e4a' }   // Step 6 - darkest
              ];
              return (
                <div key={index} className="relative">
                  <div 
                    className="text-white px-5 py-3 rounded-xl flex items-center gap-3"
                    style={{
                      background: `linear-gradient(135deg, ${colors[index].from} 0%, ${colors[index].to} 100%)`
                    }}
                  >
                    <span className="text-2xl font-bold opacity-60">{item.step}</span>
                    <div>
                      <p className="font-semibold text-sm">{item.title}</p>
                      <p className="text-[10px] text-teal-100">
                        {item.hasLink ? (
                          <>
                            <a href="#contact" className="underline hover:text-white">フォーム</a>
                            から
                          </>
                        ) : item.description}
                      </p>
                    </div>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="flex justify-center py-0.5">
                      <div className="w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-teal-600"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section - Centered */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-slate-100 border-slate-200 shadow-none">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-5">
                監修を入れるなら、
                <br />
                進め方まで整えませんか
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                監修者を立てること自体は決まっている。
                <br />
                ただ、その先で、
              </p>
              <ul className="space-y-1 mb-4 text-slate-700 inline-block text-left">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">・</span>
                  <span>誰に依頼するのが適切か</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">・</span>
                  <span>どこまで監修に含めるべきか</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">・</span>
                  <span>修正や進行をどう整理すればよいか</span>
                </li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                で、手が止まることは少なくありません。
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                記事監修マッチは、
                <br />
                監修者の選定から進行管理までを一括で設計し、
                <br />
                監修を「迷わず進められる工程」に整えます。
              </p>
              <p className="text-slate-800 font-medium mb-6">
                まずは状況を伺い、
                <br />
                進め方を一緒に整理させてください。
              </p>
              
              <div className="space-y-1 text-sm text-slate-500 mb-6">
                <p>※相談のみでも費用はかかりません</p>
                <p>※その場での発注判断は不要です</p>
                <p>※1記事だけのご相談も歓迎します</p>
              </div>

              <Button 
                onClick={scrollToContact}
                size="lg"
                className="free_consultation_button bg-teal-600 hover:bg-teal-700 text-white px-10 py-5 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              >
                無料で相談する
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-10 text-center">
            記事監修サービスのよくあるご質問
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border border-slate-200 rounded-xl px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left text-slate-800 font-medium hover:no-underline py-5">
                  <span className="flex items-start gap-3">
                    <span className="text-teal-600 font-bold">Q.</span>
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  <div className="flex items-start gap-3 pl-0">
                    <span className="text-slate-400 font-bold">A.</span>
                    <span>{faq.answer}</span>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 px-6 bg-slate-50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3 text-center">
            無料で相談する
          </h2>
          <p className="text-slate-600 text-center mb-8 text-sm">
            以下のフォームよりお気軽にご相談ください。
            <br />
            1営業日以内にご連絡いたします。
          </p>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-slate-700 text-sm">
                    会社名 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    placeholder="株式会社〇〇"
                    className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 text-sm">
                    お名前 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="山田 太郎"
                    className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 text-sm">
                    メールアドレス <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="example@company.co.jp"
                    className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 text-sm">
                    電話番号
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="03-0000-0000"
                    className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="text-slate-700 text-sm">
                    ご相談の分野
                  </Label>
                  <Select onValueChange={handleSelectChange}>
                    <SelectTrigger className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">IT・BtoB領域</SelectItem>
                      <SelectItem value="real-estate">不動産・住宅</SelectItem>
                      <SelectItem value="finance">金融・保険</SelectItem>
                      <SelectItem value="medical">医療・ヘルスケア</SelectItem>
                      <SelectItem value="legal">法律・士業</SelectItem>
                      <SelectItem value="lifestyle">生活・専門ニッチ</SelectItem>
                      <SelectItem value="other">その他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 text-sm">
                    ご相談内容 <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="監修をご検討中の記事の内容や、現在の課題などをお聞かせください。"
                    rows={4}
                    className="bg-white border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="application_button w-full bg-teal-600 hover:bg-teal-700 text-white py-5 text-base rounded-xl transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  ※相談のみでも費用はかかりません。その場での発注判断は不要です。
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* E-E-A-T Checker Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-teal-400 text-sm font-semibold mb-3">関連サービス</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              E-E-A-Tスコアを可視化する
            </h2>
            <p className="text-slate-400 text-sm md:text-base max-w-2xl mx-auto">
              記事のE-E-A-T（経験・専門性・権威性・信頼性）+独自性を5段階でスコア化。<br className="hidden md:inline" />
              改善ポイントを具体的にレポートします。
            </p>
          </div>
          
          <a 
            href="https://eeat-checker.miwrite-match.jp/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden shadow-2xl hover:border-teal-500/50 transition-all duration-300 hover:shadow-teal-500/10">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 md:p-10">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  {/* Left: Service Info */}
                  <div className="flex-1 text-center lg:text-left">
                    <p className="text-xl font-bold text-white mb-4">E-E-A-Tチェッカー</p>
                    <p className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
                      E-E-A-T＋独自性を<br className="hidden md:inline" />
                      <span className="text-teal-400">数値で可視化</span>
                    </p>
                    <ul className="space-y-2 text-slate-300 text-sm mb-6">
                      <li className="flex items-center gap-2 justify-center lg:justify-start">
                        <CheckCircle2 className="w-4 h-4 text-teal-400" />
                        記事を5観点・5段階でスコア化
                      </li>
                      <li className="flex items-center gap-2 justify-center lg:justify-start">
                        <CheckCircle2 className="w-4 h-4 text-teal-400" />
                        改善ポイントを具体的にレポート
                      </li>
                      <li className="flex items-center gap-2 justify-center lg:justify-start">
                        <CheckCircle2 className="w-4 h-4 text-teal-400" />
                        月2記事まで無料・初期費用なし
                      </li>
                    </ul>
                    <span className="inline-flex items-center gap-2 text-teal-400 font-semibold">
                      無料で診断してみる
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                  
                  {/* Right: Radar Chart Visual */}
                  <div className="flex-shrink-0">
                    <div className="w-64 h-64 md:w-72 md:h-72 bg-slate-900/80 rounded-xl border border-slate-700 flex items-center justify-center relative overflow-hidden">
                      {/* Simplified Radar Chart Visual */}
                      <svg viewBox="0 0 240 200" className="w-full h-full p-2">
                        {/* Pentagon background */}
                        <polygon 
                          points="120,25 195,70 175,155 65,155 45,70" 
                          fill="none" 
                          stroke="#334155" 
                          strokeWidth="1"
                        />
                        <polygon 
                          points="120,42 175,75 160,140 80,140 65,75" 
                          fill="none" 
                          stroke="#334155" 
                          strokeWidth="1"
                        />
                        <polygon 
                          points="120,58 155,80 145,125 95,125 85,80" 
                          fill="none" 
                          stroke="#334155" 
                          strokeWidth="1"
                        />
                        {/* Data polygon */}
                        <polygon 
                          points="120,35 180,68 168,145 75,140 60,72" 
                          fill="rgba(20, 184, 166, 0.3)" 
                          stroke="#14b8a6" 
                          strokeWidth="2"
                        />
                        {/* Labels */}
                        <text x="120" y="15" textAnchor="middle" className="fill-slate-400" style={{fontSize: '11px'}}>独自性</text>
                        <text x="202" y="72" textAnchor="start" className="fill-slate-400" style={{fontSize: '11px'}}>専門性</text>
                        <text x="180" y="168" textAnchor="middle" className="fill-slate-400" style={{fontSize: '11px'}}>経験</text>
                        <text x="60" y="168" textAnchor="middle" className="fill-slate-400" style={{fontSize: '11px'}}>権威性</text>
                        <text x="38" y="72" textAnchor="end" className="fill-slate-400" style={{fontSize: '11px'}}>信頼性</text>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="https://eeat-checker.miwrite-match.jp/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="eeat_checker_button inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/20"
            >
              まずは無料で診断してみる
              <ArrowRight className="w-5 h-5" />
            </a>
            <p className="text-slate-500 text-sm">
              月2記事まで無料・初期費用なし
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img 
                  src="/images/assets/hupg3mur-Group.svg" 
                  alt="記事監修マッチ" 
                  className="h-6 brightness-0 invert"
                />
                <span className="text-xs text-slate-400 ml-2">by ミライトマッチ</span>
              </div>
              <p className="text-slate-400 text-sm">
                株式会社IT&Plucktice
              </p>
              <a 
                href="https://miwrite-match.jp/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 text-sm hover:text-white transition-colors"
              >
                https://miwrite-match.jp/
              </a>
            </div>
            <p className="text-slate-500 text-sm">
              ※営業目的のみのご連絡は行っておりません。
            </p>
          </div>
          <Separator className="my-6 bg-slate-700" />
          <p className="text-slate-500 text-sm text-center">
            © {new Date().getFullYear()} 記事監修マッチ All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
